import React, { useEffect, useState } from 'react';
import { useGlobal } from '../../global/GlobalContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  FiPackage,
  FiMapPin,
  FiUser,
  FiHelpCircle,
  FiBookOpen,
  FiLogOut,
  FiChevronRight,
} from 'react-icons/fi';
import Skeleton from 'react-loading-skeleton';
import { UserRole } from '../../config/userRole';

const QUICK_LINKS = [
  { label: 'Orders', description: 'View, modify, and track orders', to: '/myaccount/orders', icon: FiPackage },
  { label: 'Addresses', description: 'Manage your delivery locations', to: '/myaccount/addresses', icon: FiMapPin },
  { label: 'Profile', description: 'Edit your personal information', to: '/myaccount/profile', icon: FiUser },
  { label: 'Help & Support', description: 'Get assistance with your queries', to: '../contact-us', icon: FiHelpCircle },
  { label: 'Our Story', description: 'Learn more about our journey', to: '../about-us', icon: FiBookOpen },
];

const OverView = () => {
  const { token } = useGlobal();
  const [customers, setCustomers] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/get_customer_details`, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        setCustomers(data.customer_data);
      } catch (error) {
        console.error('Error fetching profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [token]);

  return (
    <div>
      <div className="max-w-5xl mx-auto">
        {/* Profile Header */}
        {loading ? (
          <div className="bg-white p-4 md:p-6 rounded-2xl border border-gray-100 shadow-sm mb-6 flex items-center gap-4">
            <Skeleton circle height={48} width={48} />
            <div className="flex-1">
              <Skeleton height={16} width={140} className="mb-2" />
              <Skeleton height={12} width={180} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-4 bg-white p-4 md:p-6 rounded-2xl border border-gray-100 shadow-sm mb-6">
            <div className="text-xl md:text-2xl font-extrabold w-12 h-12 md:w-14 md:h-14 rounded-full bg-brand-600 text-white uppercase flex justify-center items-center shrink-0">
              {customers?.first_name?.charAt(0)}
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-base md:text-xl font-bold text-gray-900 truncate">{customers?.first_name}</h1>
                {UserRole.userRole === 'distributor' && (
                  <span className="text-[11px] font-semibold text-brand-600 bg-brand-600/10 px-2 py-0.5 rounded-full capitalize">
                    {UserRole.userRole}
                  </span>
                )}
              </div>
              <p className="text-gray-500 text-sm truncate">{customers?.email}</p>
              {customers?.mobile_number && (
                <p className="text-gray-500 text-sm">{customers?.mobile_number}</p>
              )}
            </div>
          </div>
        )}

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
          {QUICK_LINKS.map((link) => {
            const LinkIcon = link.icon;
            return (
              <Link
                key={link.label}
                to={link.to}
                className="bg-white p-4 md:p-6 rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-600/30 transition-all text-center flex flex-col items-center gap-2 md:gap-3"
              >
                <div className="w-11 h-11 md:w-12 md:h-12 rounded-full bg-brand-600/10 text-brand-600 flex items-center justify-center">
                  <LinkIcon size={20} />
                </div>
                <h2 className="text-sm md:text-base font-semibold text-gray-900">{link.label}</h2>
                <p className="text-xs text-gray-500 hidden md:block">{link.description}</p>
              </Link>
            );
          })}
        </div>

        <button
          className="md:hidden w-full mt-4 flex items-center justify-between gap-2 text-red-600 bg-red-50 border border-red-100 rounded-2xl px-4 py-3 font-medium text-sm"
          onClick={() => navigate('/logout')}
        >
          <span className="flex items-center gap-2">
            <FiLogOut size={18} /> Logout
          </span>
          <FiChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default OverView;
